import { useState } from 'react';
import { t, tn } from '@app/i18n';
import { Alert, ErrorBanner, PageHeader } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { dataHour } from '@app/format';
import { identitiesKeys } from '@app/main/Identities/Identities.api';
import { useClusterReading } from '@app/reading';
import { overviewKeys, useOverview, type Overview } from './Overview.api';
import { ActivityCard } from './ActivityCard';
import { ConnectionCard } from './ConnectionCard';
import { DiscoveryCard } from './DiscoveryCard';
import { DiscoveryNowButton } from './DiscoveryNowButton';
import { FirstStepsCard } from './FirstStepsCard';
import { KpiCards } from './KpiCards';
import { LastDecisionsCard } from './LastDecisionsCard';
import { OverviewSkeleton } from './OverviewSkeleton';
import s from './Overview.module.css';

/**
 * T3 — Visão geral. A primeira tela depois do login (AC-01).
 *
 * Quatro indicadores e cinco cards: Conexão com o cliente, Discovery, Atividade
 * 24 h, Primeiros passos e as 6 últimas decisões (FR-094). A leitura **nunca** é
 * 502: cada card que depende do middleware degrada sozinho, via `falhas[]`
 * (FR-095) — o `ErrorBanner` do topo só aparece quando a própria leitura local
 * falhou.
 *
 * # Como a tela abre (Lote 2b; aim-6ph.13)
 *
 * Uma leitura só, e ela responde na hora: os contadores locais e o estado do
 * cluster saem do Postgres, este da foto do runtime (regra 11). Até ela chegar, o
 * `OverviewSkeleton` desenha a página inteira com as mesmas caixas do conteúdo
 * real — a primeira tela depois do login não é uma linha de «Carregando…» seguida
 * de um salto de layout.
 *
 * Ao abrir, a tela pede a leitura nova do cluster (`useClusterReading`, decisão 2).
 * O SecurityReady e o card Conexão mostram a foto na hora, com «lido às», dizem que
 * estão lendo de novo, e se atualizam quando a leitura termina (o `refetchInterval`
 * de `useOverview`). A lista de T4 também lê esta foto, e por isso é relida junto.
 *
 * Cortes em relação ao protótipo (`rOverview`): sem "Restaurar dados de exemplo"
 * (era o reset do seed do protótipo) e sem o detalhe por item dos Primeiros
 * passos — o contrato manda rótulo, estado e link, não a contagem.
 */

/** `Último Discovery {quando} · {n} imagens varridas` — a linha `upd` do protótipo. */
function discoveryRow(overview: Overview): string {
  const quando = dataHour(overview.discovery?.last_execution_at);
  const swept = overview.discovery?.scanned_images;
  const parts = [
    t('overview.page.lastDiscovery', {
      when: quando ?? t('overview.page.lastDiscovery.never'),
    }),
  ];
  // «1 imagens varridas» era o que saía no singular.
  if (swept !== null && swept !== undefined) {
    parts.push(tn('overview.page.lastDiscovery.swept', swept));
  }
  return parts.join(' · ');
}

export function OverviewPage() {
  const reading = useOverview();
  const cluster = useClusterReading({
    scope: 'cluster',
    refresh: [overviewKeys.overview, identitiesKeys.todas],
  });
  const [erroDiscovery, setErroDiscovery] = useState<ApiError | null>(null);
  const [avisoDiscovery, setAvisoDiscovery] = useState<string | null>(null);

  // Os contadores locais (identidades, fila, atividade, primeiros passos) são fato
  // ainda que ninguém tenha lido o cluster, e são a maior parte da tela. O que não
  // se sabe já sai nulo do servidor, e cada card desenha o seu «—».
  const overview = reading.data;
  const failed = reading.isError;

  return (
    <>
      <PageHeader
        title={t('overview.page.title')}
        icon="gauge"
        tip={t('overview.page.purpose')}
        updatedLabel={overview ? discoveryRow(overview) : undefined}
        actions={<DiscoveryNowButton onErro={setErroDiscovery} onAviso={setAvisoDiscovery} />}
      />

      {/* O banner do topo só aparece quando a própria leitura local caiu, e não há
          tela para degradar por card. */}
      {failed ? (
        <ErrorBanner
          error={reading.error}
          title={t('overview.page.error')}
          onRetry={() => {
            void reading.refetch();
          }}
        />
      ) : null}

      {/* Falha ao **disparar** a varredura: o banner diz o código (FR-123). */}
      {erroDiscovery ? (
        <ErrorBanner error={erroDiscovery} title={t('overview.page.discovery.error')} escrita />
      ) : null}

      {/* Falha ou aviso da varredura **terminada**. Desde 29/08 ela corre fora da
          requisição, então não volta num 502 — se este alerta não existisse, um
          `registry_ilegivel` sumiria da tela junto com o toast. */}
      {avisoDiscovery ? (
        <Alert tone="warning" title={t('overview.page.discovery.warning')}>
          {avisoDiscovery}
        </Alert>
      ) : null}

      {/* Esqueleto enquanto a leitura não chegou. Ele ocupa exatamente as caixas
          do conteúdo real — a troca não move nada abaixo. */}
      {!overview && !failed ? <OverviewSkeleton /> : null}

      {overview ? (
        <>
          <KpiCards overview={overview} readingPending={cluster.pending} />
          <div className={s.grade}>
            <ConnectionCard overview={overview} readingPending={cluster.pending} />
            {/* Segunda linha: os dois cards que faltavam. O de Discovery não recebe
                a visão geral — ele lê as duas rotas de discovery que o console já
                consulta (`getDiscoveryConfig` e `getDiscoveryStatus`). */}
            <DiscoveryCard />
            <ActivityCard overview={overview} />
            {/* Ordem = ordem visual, e a fila é fechada por quem vem depois. Estes
                dois dividem a terceira fila a ⅓ / ⅔ (2 + 4 das 6 colunas): antes os
                «Primeiros passos» ficavam sozinhos ali, com duas colunas em branco
                ao lado, e as «Últimas decisões» tomavam uma fila só para si
                (pedido do mrolim, 09/09). */}
            <FirstStepsCard overview={overview} />
            <div className={s.larga}>
              <LastDecisionsCard overview={overview} />
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
